import React, { useMemo } from 'react';
import { parse } from 'yaml';
import DatabaseNode from './DatabaseNode';

interface SoleOutputVisualizerProps {
  soleOutput: string;
}

interface SoleObjectProperties {
  name: string;
  database?: string;
  schema?: string;
  columns?: Record<string, any>;
  statement?: string;
  return_type?: string;
  arguments?: Record<string, any>;
  execute_as?: string;
  comment?: string;
  language?: string;
  manage_mode?: string;
  with_tags?: Record<string, any>;
  is_managed?: boolean;
  is_transient?: boolean;
  change_tracking?: boolean;
  [key: string]: any;
}

interface ParsedItem {
  [type: string]: SoleObjectProperties;
}

interface ProcessedData {
  databases: Record<
    string,
    {
      schemas: Record<
        string,
        {
          tables?: Record<string, any>;
          views?: Record<string, any>;
          procedures?: Record<string, any>;
          functions?: Record<string, any>;
          [key: string]: any;
        }
      >;
    }
  >;
}

export default function SoleOutputVisualizer(props: SoleOutputVisualizerProps): JSX.Element {
  const { soleOutput } = props;

  const parsedData = useMemo(() => {
    try {
      // Parse the YAML string
      const parsed = parse(soleOutput) as ParsedItem[];

      // Process the new format into the structure expected by the components
      const processedData: ProcessedData = { databases: {} };

      // Create a map to store objects by their references
      const objectMap: Record<string, SoleObjectProperties> = {};

      // First pass: collect all objects by their type and name for reference resolution
      if (Array.isArray(parsed)) {
        parsed.forEach((item) => {
          // Each item is an object with a single key (the type) and its properties
          const entries = Object.entries(item);
          if (entries.length === 0) return;

          const [type, properties] = entries[0] as [string, SoleObjectProperties];

          if (
            properties !== undefined &&
            properties !== null &&
            typeof properties.name === 'string' &&
            properties.name !== ''
          ) {
            // Store the object for reference resolution
            objectMap[`${type}.${properties.name}`] = properties;
          }
        });
      }

      // Helper function to resolve references like rel(database.A_CICD)
      const resolveReference = (ref: string | undefined): string | null => {
        if (typeof ref !== 'string') return null;

        const match = ref.match(/rel\((.*?)\.(.*?)\)/);
        if (match !== null) {
          const [, , name] = match;
          return name;
        }
        return ref;
      };

      // Second pass: build the hierarchical structure
      if (Array.isArray(parsed)) {
        parsed.forEach((item) => {
          const entries = Object.entries(item);
          if (entries.length === 0) return;

          const [type, properties] = entries[0] as [string, SoleObjectProperties];

          if (
            properties === undefined ||
            properties === null ||
            typeof properties.name !== 'string' ||
            properties.name === ''
          )
            return;

          if (type === 'database') {
            // Add database to the structure
            const dbName = properties.name;
            if (typeof dbName === 'string' && dbName !== '') {
              processedData.databases[dbName] = { schemas: {} };
            }
          } else if (type === 'schema') {
            // Add schema to its database
            const schemaName = properties.name;
            const dbRef = properties.database;
            const dbName = resolveReference(dbRef);

            if (typeof dbName === 'string' && dbName !== '') {
              if (processedData.databases[dbName] === undefined) {
                processedData.databases[dbName] = { schemas: {} };
              }

              if (typeof schemaName === 'string' && schemaName !== '') {
                processedData.databases[dbName].schemas[schemaName] = {};
              }
            }
          } else if (['table', 'view', 'procedure', 'function', 'file_format', 'sequence'].includes(type)) {
            // Add object to its schema
            const objectName = properties.name;
            const dbRef = properties.database;
            const schemaRef = properties.schema;

            const dbName = resolveReference(dbRef);
            const schemaName = resolveReference(schemaRef);

            if (typeof dbName !== 'string' || dbName === '' || typeof schemaName !== 'string' || schemaName === '') {
              return;
            }

            // Ensure the database and schema exist
            if (processedData.databases[dbName] === undefined) {
              processedData.databases[dbName] = { schemas: {} };
            }

            if (processedData.databases[dbName].schemas[schemaName] === undefined) {
              processedData.databases[dbName].schemas[schemaName] = {};
            }

            // Add the object to the appropriate collection in the schema
            const pluralType = `${type}s`;
            if (processedData.databases[dbName].schemas[schemaName][pluralType] === undefined) {
              processedData.databases[dbName].schemas[schemaName][pluralType] = {};
            }

            if (typeof objectName === 'string' && objectName !== '') {
              processedData.databases[dbName].schemas[schemaName][pluralType][objectName] = properties;
            }
          }
        });
      }

      return processedData;
    } catch (error) {
      console.error('Error parsing YAML:', error);
      return null;
    }
  }, [soleOutput]);

  if (parsedData === null || typeof parsedData !== 'object' || typeof parsedData.databases !== 'object') {
    return <div className="text-red-500">Error parsing SOLE configuration</div>;
  }

  const databaseKeys = Object.keys(parsedData.databases);
  const hasDatabases = databaseKeys.length > 0;

  return (
    <div className="w-full max-w-[30rem] flex flex-col items-center space-y-1">
      {hasDatabases ? (
        Object.entries(parsedData.databases).map(([dbName, dbData]: [string, any]) => (
          <DatabaseNode key={dbName} name={dbName} data={dbData} />
        ))
      ) : (
        <div className="text-gray-600">No objects selected</div>
      )}
    </div>
  );
}
